import { useState } from "react";
import { useModelPricing, useUpdateModelPricing, useModelUsageHistory } from "@/hooks/use-pricing-admin";

const STARTER_RATE = 0.00900; // $90 / 10k credits (Starter, matches live Stripe checkout)
const PRO_RATE = 199 / 25000;      // 0.00796
const AGENCY_RATE = 299 / 45000;   // 0.006644...

function marginAtRate(realCostUsd: number, credits: number, rate: number) {
  const charged = credits * rate;
  return charged > 0 ? ((charged - realCostUsd) / charged) * 100 : 0;
}

type PricingRow = {
  id: number;
  provider: string;
  modelKey: string;
  modelLabel: string;
  unitType: string;
  realCostUsd: string;
  mode: "markup_pct" | "fixed_credits";
  markupX: string;
  creditsPerUnit: string;
  isArchived: boolean;
  updatedAt?: string;
  updatedByUserId?: number | null;
};

type DraftPatch = Partial<Pick<PricingRow, "realCostUsd" | "mode" | "markupX" | "creditsPerUnit">>;

function computeDerived(d: Pick<PricingRow, "realCostUsd" | "mode" | "markupX" | "creditsPerUnit">) {
  const realCost = Number(d.realCostUsd);
  if (d.mode === "markup_pct") {
    const markup = Number(d.markupX);
    const credits = realCost > 0 && markup > 0 ? (realCost * markup) / STARTER_RATE : 0;
    const charged = credits * STARTER_RATE;
    const margin = charged > 0 ? ((charged - realCost) / charged) * 100 : 0;
    return { credits, charged, margin };
  }
  const credits = Number(d.creditsPerUnit);
  const charged = credits * STARTER_RATE;
  const margin = charged > 0 ? ((charged - realCost) / charged) * 100 : 0;
  return { credits, charged, margin };
}

function groupRows(rows: PricingRow[]) {
  const groups: Record<string, PricingRow[]> = {
    "LLM models (OpenRouter)": [],
    "Image generation (WaveSpeed)": [],
    "Embeddings": [],
    "Scraping": [],
  };
  for (const r of rows) {
    if (r.provider === "openrouter") groups["LLM models (OpenRouter)"].push(r);
    else if (r.provider === "wavespeed") groups["Image generation (WaveSpeed)"].push(r);
    else if (r.provider === "openai_embeddings") groups["Embeddings"].push(r);
    else if (r.provider === "firecrawl" || r.provider === "apify") groups["Scraping"].push(r);
    else (groups["LLM models (OpenRouter)"] as any).push(r);  // fallback
  }
  return groups;
}

function UsageHistoryModal({ modelId, modelLabel, onClose }: { modelId: number; modelLabel: string; onClose: () => void }) {
  const q = useModelUsageHistory(modelId, 30);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded bg-white p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{modelLabel} — last 30 days</h2>
          <button onClick={onClose} className="text-[#526074] hover:text-neutral-800">✕</button>
        </div>

        {q.isLoading && <div className="text-sm text-[#526074]">Loading…</div>}
        {q.error && <div className="text-sm text-red-600">Failed to load usage.</div>}
        {q.data && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-4 gap-2 rounded bg-neutral-50 p-3 text-xs">
              <div>
                <div className="text-[#526074]">Calls</div>
                <div className="font-mono text-base">{q.data.summary?.totalCalls ?? 0}</div>
              </div>
              <div>
                <div className="text-[#526074]">Credits</div>
                <div className="font-mono text-base">{Number(q.data.summary?.totalCredits ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-[#526074]">Real cost</div>
                <div className="font-mono text-base">${Number(q.data.summary?.totalRealUsd ?? 0).toFixed(4)}</div>
              </div>
              <div>
                <div className="text-[#526074]">Unique users</div>
                <div className="font-mono text-base">{q.data.summary?.uniqueUsers ?? 0}</div>
              </div>
            </div>

            <div>
              <div className="mb-2 text-xs uppercase text-[#526074]">Recent events</div>
              {q.data.recent.length === 0 ? (
                <div className="text-[#526074]">No events in the last 30 days.</div>
              ) : (
                <div className="space-y-1 text-xs">
                  {q.data.recent.map((e) => (
                    <div key={e.id} className="flex items-center justify-between rounded border border-[#DCE3ED] p-2">
                      <div>
                        <div>user {e.userId} · {Number(e.inputUnits).toFixed(2)} in / {Number(e.outputUnits).toFixed(2)} out</div>
                        <div className="text-[11px] text-[#526074]">{new Date(e.createdAt).toLocaleString()} · {e.latencyMs ?? "—"}ms</div>
                      </div>
                      <div className="font-mono tabular-nums">{Number(e.creditsCharged).toFixed(4)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function PricingModelsTab() {
  const { data, isLoading, error } = useModelPricing();
  const update = useUpdateModelPricing();
  const [drafts, setDrafts] = useState<Record<number, DraftPatch>>({});
  const [showPerPlanMargin, setShowPerPlanMargin] = useState(false);
  const [historyForId, setHistoryForId] = useState<{ id: number; label: string } | null>(null);

  if (isLoading) return <div className="admin-state p-6 text-sm text-[#526074]">Loading…</div>;
  if (error) return <div className="admin-state p-6 text-sm text-red-600">Failed to load models: {String(error)}</div>;

  const rows: PricingRow[] = data?.rows ?? [];
  const grouped = groupRows(rows);

  const mostRecentRow = rows.reduce<PricingRow | null>((best, r) => {
    if (!best) return r;
    return new Date(r.updatedAt ?? 0) > new Date(best.updatedAt ?? 0) ? r : best;
  }, null);

  const draftOf = (r: PricingRow): Pick<PricingRow, "realCostUsd" | "mode" | "markupX" | "creditsPerUnit"> => {
    const d = drafts[r.id] ?? {};
    return {
      realCostUsd: d.realCostUsd ?? r.realCostUsd,
      mode: d.mode ?? r.mode,
      markupX: d.markupX ?? r.markupX,
      creditsPerUnit: d.creditsPerUnit ?? r.creditsPerUnit,
    };
  };

  const isDirty = (r: PricingRow) => !!drafts[r.id] && Object.keys(drafts[r.id] || {}).length > 0;

  const setDraft = (id: number, patch: DraftPatch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...patch } }));

  const resetDraft = (id: number) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const save = async (r: PricingRow) => {
    const d = draftOf(r);
    const derived = computeDerived(d);
    // In markup_pct mode we persist the computed credits; in fixed_credits mode we persist user input.
    const patch = {
      realCostUsd: d.realCostUsd,
      mode: d.mode,
      markupX: d.markupX,
      creditsPerUnit: d.mode === "markup_pct" ? String(derived.credits) : d.creditsPerUnit,
    };
    await update.mutateAsync({ id: r.id, patch });
    resetDraft(r.id);
  };

  return (
    <div className="space-y-6">
      <section className="rounded border border-[#DCE3ED] bg-white p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#526074]">Reference rate</div>
            <div className="mt-1 font-mono text-sm">${STARTER_RATE.toFixed(5)} / credit</div>
            <div className="text-[11px] text-[#526074]">Starter plan baseline</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#526074]">Default markup</div>
            <div className="mt-1 font-mono text-sm">2.0×</div>
            <div className="text-[11px] text-[#526074]">Applied to new rows</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#526074]">Last updated</div>
            <div className="mt-1 text-sm">
              {mostRecentRow?.updatedAt
                ? new Date(mostRecentRow.updatedAt).toLocaleString()
                : "—"}
            </div>
            <div className="text-[11px] text-[#526074]">
              {mostRecentRow?.updatedByUserId ? `by admin ${mostRecentRow.updatedByUserId}` : ""}
            </div>
          </div>
        </div>
        <label className="mt-4 inline-flex items-center gap-2 text-xs text-[#526074]">
          <input
            type="checkbox"
            checked={showPerPlanMargin}
            onChange={(e) => setShowPerPlanMargin(e.target.checked)}
            data-testid="models-per-plan-toggle"
          />
          Show per-plan margin (Pro + Agency)
        </label>
      </section>

      {Object.entries(grouped).map(([label, groupRows]) => {
        if (groupRows.length === 0) return null;
        return (
          <section key={label} className="rounded border border-[#DCE3ED] bg-white">
            <div className="border-b border-[#DCE3ED] bg-neutral-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[#526074]">
              {label}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[#526074]">
                  <tr>
                    <th className="px-4 py-2 font-medium">Model</th>
                    <th className="px-4 py-2 font-medium">Unit</th>
                    <th className="px-4 py-2 font-medium">Real cost</th>
                    <th className="px-4 py-2 font-medium">Mode</th>
                    <th className="px-4 py-2 font-medium">Markup / Credits</th>
                    <th className="px-4 py-2 font-medium">Credits/unit</th>
                    <th className="px-4 py-2 font-medium">Charged $</th>
                    <th className="px-4 py-2 font-medium">Margin</th>
                    {showPerPlanMargin && <th className="px-4 py-2 font-medium">Pro margin</th>}
                    {showPerPlanMargin && <th className="px-4 py-2 font-medium">Agency margin</th>}
                    <th className="px-4 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((r) => {
                    const d = draftOf(r);
                    const derived = computeDerived(d);
                    const dirty = isDirty(r);
                    const marginWarn = derived.margin < 40 && derived.margin !== 0;
                    return (
                      <tr key={r.id} className="border-t border-[#DCE3ED]">
                        <td className="px-4 py-2 font-medium text-neutral-800">{r.modelLabel}</td>
                        <td className="px-4 py-2 text-[#526074]">{r.unitType}</td>
                        <td className="px-4 py-2">
                          <input
                            className="w-24 rounded border border-[#DCE3ED] px-2 py-1 font-mono text-xs"
                            value={d.realCostUsd}
                            onChange={(e) => setDraft(r.id, { realCostUsd: e.target.value })}
                            data-testid={`model-${r.id}-realcost`}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <button
                            className="rounded border border-[#DCE3ED] bg-white px-2 py-1 text-xs hover:bg-[#F1F5F9]"
                            onClick={() =>
                              setDraft(r.id, {
                                mode: d.mode === "markup_pct" ? "fixed_credits" : "markup_pct",
                              })
                            }
                            data-testid={`model-${r.id}-mode`}
                          >
                            {d.mode === "markup_pct" ? "% markup" : "fixed"}
                          </button>
                        </td>
                        <td className="px-4 py-2">
                          {d.mode === "markup_pct" ? (
                            <input
                              className="w-20 rounded border border-[#DCE3ED] px-2 py-1 font-mono text-xs"
                              value={d.markupX}
                              onChange={(e) => setDraft(r.id, { markupX: e.target.value })}
                              data-testid={`model-${r.id}-markup`}
                              placeholder="2.0"
                            />
                          ) : (
                            <input
                              className="w-20 rounded border border-[#DCE3ED] px-2 py-1 font-mono text-xs"
                              value={d.creditsPerUnit}
                              onChange={(e) => setDraft(r.id, { creditsPerUnit: e.target.value })}
                              data-testid={`model-${r.id}-credits`}
                            />
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-neutral-700">
                          {derived.credits.toFixed(4)}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-neutral-700">
                          ${derived.charged.toFixed(4)}
                        </td>
                        <td
                          className={`px-4 py-2 text-right font-mono text-xs tabular-nums ${
                            marginWarn ? "text-red-600" : "text-neutral-700"
                          }`}
                        >
                          {derived.margin.toFixed(1)}%
                        </td>
                        {showPerPlanMargin && (
                          <>
                            <td className={`px-4 py-2 text-right font-mono text-xs tabular-nums ${marginAtRate(Number(d.realCostUsd), derived.credits, PRO_RATE) < 40 ? "text-red-600" : "text-neutral-700"}`}>
                              {marginAtRate(Number(d.realCostUsd), derived.credits, PRO_RATE).toFixed(1)}%
                            </td>
                            <td className={`px-4 py-2 text-right font-mono text-xs tabular-nums ${marginAtRate(Number(d.realCostUsd), derived.credits, AGENCY_RATE) < 40 ? "text-red-600" : "text-neutral-700"}`}>
                              {marginAtRate(Number(d.realCostUsd), derived.credits, AGENCY_RATE).toFixed(1)}%
                            </td>
                          </>
                        )}
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              className="rounded border border-[#DCE3ED] bg-white px-2 py-1 text-xs hover:bg-[#F1F5F9]"
                              onClick={() => setHistoryForId({ id: r.id, label: r.modelLabel })}
                              data-testid={`model-${r.id}-history`}
                            >
                              History
                            </button>
                            {dirty ? (
                              <>
                                <button
                                  className="rounded border border-[#DCE3ED] bg-white px-2 py-1 text-xs hover:bg-[#F1F5F9]"
                                  onClick={() => resetDraft(r.id)}
                                  disabled={update.isPending}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="rounded bg-black px-3 py-1 text-xs text-white disabled:opacity-40"
                                  onClick={() => save(r)}
                                  disabled={update.isPending}
                                  data-testid={`model-${r.id}-save`}
                                >
                                  {update.isPending ? "Saving…" : "Save"}
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      <div className="text-xs text-[#526074]">
        Margin is computed against the Starter plan's reference rate (${STARTER_RATE.toFixed(5)}/credit). Rows below 40% margin are flagged red.
      </div>

      {historyForId && (
        <UsageHistoryModal
          modelId={historyForId.id}
          modelLabel={historyForId.label}
          onClose={() => setHistoryForId(null)}
        />
      )}
    </div>
  );
}
